import datapoints from './datapoints';
import createItemContextsForDeleteStoreUpdate from './helpers/createItemContextsForDeleteStoreUpdate';
import showNotificationOnSchedule from './helpers/showNotificationOnSchedule';
import scheduleItemsService from '../services/scheduleItemsService';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { OwaDate } from 'owa-datetime';
import { getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import { handleServerResponseSuccessAndError } from 'owa-mail-triage-action-response-processor';
import * as undoActions from 'owa-mail-undo';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import deleteItemsStoreUpdate, {
    ItemContext,
} from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';

/**
 * Schedule or Unschedule items
 * @param rowKeys the row keys
 * @param tableView the tableView
 * @param actionSource the actionSource
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 */
export default createLegacyActionWithDatapoint('scheduleItems', datapoints.scheduleItems)(
    async function scheduleItems(
        rowKeys: string[],
        tableView: TableView,
        actionSource: ActionSource,
        scheduleReturnTime: OwaDate | undefined
    ): Promise<void> {
        // Get the dedupped item ids to update, as multiple rowKeys could map to the same rowId
        let itemIdsToSchedule = getRowIdsFromRowKeys(rowKeys, tableView.id);
        if (itemIdsToSchedule.length === 0) {
            // No need to send out the service call
            return Promise.resolve();
        }

        const folderId = tableView.tableQuery.folderId;
        const isScheduledFolder = folderIdToName(folderId) === 'scheduled';

        undoActions.clearLastUndoableAction();
        showNotificationOnSchedule(!isScheduledFolder, scheduleReturnTime);

        // Optimistically delete items on client.
        // *ONLY* if we are not in the scheduled folder.
        if (!isScheduledFolder) {
            const itemContexts: ItemContext[] = createItemContextsForDeleteStoreUpdate(
                itemIdsToSchedule
            );
            deleteItemsStoreUpdate(itemContexts, folderId);
        }

        const scheduleItemsPromise = scheduleItemsService(itemIdsToSchedule, scheduleReturnTime);

        // DO not allow undo within scheduled as we have no way to recover ReturnTime
        if (!isScheduledFolder) {
            undoActions.addActionToUndoStack(async () => {
                let itemIdsToUndo: string[] = [];
                const responses = await scheduleItemsPromise;
                for (const response of responses) {
                    if (response.ResponseClass === 'Success') {
                        itemIdsToUndo.push(response.Item.ItemId.Id);
                    }
                }

                if (itemIdsToUndo.length > 0) {
                    // Perform unschedule
                    await scheduleItemsService(itemIdsToUndo);
                }
                return Promise.resolve();
            }, tableView.serverFolderId);
        }

        return scheduleItemsPromise
            .then(response => {
                return handleServerResponseSuccessAndError(response);
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }
);
